/**
 * Seam context — the wovin data-plane API handed to plugins.
 *
 * Two layers: load-time ctx binds to the local app thread; each mount derives
 * a ctx bound to the actual display thread (previews render read-only).
 */
import type { Thread } from '@wovin/core'
import { lastWriteWins, liveFilterAndMap, liveQuery, query, withoutDeleted } from '@wovin/core'
import { createSignal, getOwner, onCleanup } from 'solid-js'
import { getApplogDB, insertApplogs } from '../../data/ApplogDB'
import { contentVlToPlaintext } from '../../data/note3-utils-nodeps'
import type { PluginContext, SeamWovinApi } from './contract'

/** Same filter stack as useCurrentThread, without needing a Solid context */
export function currentOf(thread: Thread): Thread {
	let t = thread
	if (!t.filters.includes('lastWriteWins')) t = lastWriteWins(t)
	if (!t.filters.includes('withoutDeleted')) t = withoutDeleted(t)
	return t
}

export function defaultRawThread(): Thread {
	return getApplogDB()
}

export function makeWovinApi(rawThreadGetter: () => Thread): SeamWovinApi {
	const cur = () => currentOf(rawThreadGetter())
	return {
		thread: cur,
		rawThread: rawThreadGetter,
		query: patterns => query(cur(), patterns) as ReturnType<SeamWovinApi['query']>,
		liveQuery: patterns => liveQuery(cur(), patterns),
		liveFilterAndMap: (pattern, mapper) => liveFilterAndMap(cur(), pattern as never, mapper as never),
		useLive: (patterns) => {
			const lq = liveQuery(cur(), patterns)
			const [nodes, setNodes] = createSignal(lq.nodes ?? [], { equals: false })
			const unsub = lq.subscribe(() => setNodes(() => lq.nodes))
			if (getOwner()) {
				onCleanup(() => {
					unsub()
					lq.dispose?.()
				})
			}
			return nodes as () => readonly never[]
		},
		insertApplogs: (applogs) => {
			const t = rawThreadGetter()
			if (t.readOnly) throw new Error('[seam] Cannot write: thread is read-only')
			return insertApplogs(t, applogs as never)
		},
		contentToPlaintext: vl => contentVlToPlaintext(vl as never),
	}
}

/** Per-mount ctx: same ui/meta, wovin re-bound to the display thread at the mount site */
export function deriveMountCtx(baseCtx: PluginContext, rawThread: Thread): PluginContext {
	return {
		...baseCtx,
		wovin: makeWovinApi(() => rawThread),
		meta: { ...baseCtx.meta, readOnly: () => !!rawThread.readOnly },
	}
}
